import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { lazyRetry } from '../lazyRetry';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PAGE_SIZE, isAbort, search, type SearchArgs } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { FacetEntry, SearchResponse } from '../types';
import { loginHref } from '../components/AccessDenied';
import ErrorState from '../components/ErrorState';
import FabricCard from '../components/FabricCard';
import { fmtDateTime, fmtMs } from '../fmt';
import Facets, { facetLabel, type FacetSelection } from '../components/Facets';
import FilterSheet, { type AppliedChip } from '../components/FilterSheet';
import { Icon } from '../components/Icon';
import Pagination from '../components/Pagination';
import QueryInterp from '../components/QueryInterp';
import { GridSkeleton, RailSkeleton } from '../components/Skeleton';
import { MaterialFilter, SupplyLockedNote, parseFibers } from '../components/SupplyFilters';
/** 단가 상한(운영자 이상 · R3-2)은 지연 청크 — 열람자 화면에는 그리지 않는다 */
const PriceFilter = lazyRetry(() => import('../components/PriceFilter'));
import ViewToggle, { useView } from '../components/ViewToggle';
import { FIBER_KO, FIELD_KO } from '../vocab';

/** 「이 검색 저장」 창(계획서 U17)은 누를 때만 내려받는다 */
const SaveSearchDialog = lazyRetry(() => import('../components/SaveSearchDialog'));

/** 0건 안내(원인 고르기 · 필터 없이 몇 건인지)는 0건일 때만 내려받는다 — 주 번들 300KB 안 (루브릭 H3) */
const ResultsEmpty = lazyRetry(() => import('../components/ResultsEmpty'));

/**
 * 주소의 조건 — 패싯 · 소재(여럿) · 단가 · 「새 원단만(after)」 을 주소에 남긴다 (결과 → 상세 → 뒤로 에서 유지, 루브릭 B3).
 * cleared — 필터 시트의 「초기화」 로 시작한 초안이다(그 적용은 「모두 지우기」 — 잠긴 조건까지 주소에서 지운다)
 */
interface Sel extends FacetSelection { materials: string[]; price_max?: number; currency?: string; after?: string; cleared?: boolean }
/**
 * canPrice — 단가 조건을 쓸 수 있는 역할(operator 이상)인가. 아니면 주소에 단가가 있어도 읽지 않는다(Codex 검토 3:
 * 공유 주소 · 강등된 사람의 저장 검색이 서버 403 으로 통째로 실패했다). 요청 · 칩 · 저장이 모두 이 값을 쓴다.
 * canSupply — 로그인했는가. 비로그인은 제조사 · 재고 조건을 읽지 않는다(서버가 401 — Codex 8차 B7 · 사양 §26.3 기본안). 주소에는 남는다.
 * 읽지 않은 조건(「잠긴 조건」)은 요청 · 칩 · 저장에서만 빠진다 — 주소는 일반 필터 변경에서 그대로 두고(applySel), 「모두 지우기」 ·
 * 시트 「초기화」 · 알림의 「… 조건 지우기」 에서만 지운다(Codex 9차 ②: 다른 필터를 바꾸면 주소의 재고 · 제조사가 지워져 로그인 뒤 되살릴 수 없었다)
 */
function readSel(p: URLSearchParams, canPrice: boolean, canSupply: boolean): Sel {
  const pm = Number(p.get('price_max'));
  const priced = canPrice && p.get('price_max') && Number.isFinite(pm) && pm > 0;
  return {
    color: p.get('color') || undefined,
    pattern: p.get('pattern') || undefined,
    manufacturer: (canSupply && p.get('manufacturer')) || undefined,
    in_stock: (canSupply && p.get('in_stock') === 'true') || undefined,
    materials: parseFibers(p.get('material')),
    price_max: priced ? pm : undefined,
    currency: priced ? (p.get('currency') === 'USD' ? 'USD' : 'KRW') : undefined,
    after: p.get('after') && !Number.isNaN(Date.parse(p.get('after')!)) ? p.get('after')! : undefined,
  };
}
function filtersOf(sel: Sel): SearchArgs['filters'] {
  return {
    manufacturer: sel.manufacturer ?? null,
    in_stock: sel.in_stock ?? null,
    color: sel.color,
    pattern: sel.pattern,
    ...(sel.materials.length ? { materials: sel.materials } : {}),
    ...(sel.price_max != null ? { price_max: sel.price_max, currency: sel.currency ?? 'KRW' } : {}),
    ...(sel.after ? { registered_after: sel.after } : {}),
  };
}
const AXES = ['color', 'pattern', 'manufacturer'] as const;

export default function Results() {
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const canPrice = role === 'operator' || role === 'admin';
  const canSupply = !!user;
  /**
   * 「질의 있음」 은 이 한 곳에서 정한다(11회차 m57 · Codex 3차 N13) — 앞뒤 공백을 뗀 값. 서버(`query.strip()`)와 같은 규칙이고,
   * 서버에도 뗀 값을 보낸다. 해석 상자 · 카드 점수 · 결과 머리 · 0건 안내 · 검색 저장이 모두 이 값을 쓴다
   * (예전에는 해석 상자만 trim 이라 `?q=%20` 에서 「점수를 붙이지 않습니다」 와 점수 1.00 이 함께 나왔다).
   */
  const q = (params.get('q') ?? '').trim();
  const hasQ = q !== '';
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const sel = readSel(params, canPrice, canSupply);
  const key = params.toString();
  /** 주소에 단가가 있는데 세션 확인 중이면 역할을 알 때까지 기다린다 — 단가를 뺀 검색 · 넣은 검색을 두 번 보내지 않게 */
  const waitRole = authLoading && params.has('price_max');
  const priceDropped = !authLoading && !canPrice && params.has('price_max');
  /** 비로그인이 제조사 · 재고 조건이 든 주소를 열었다 — 빼고 찾으며 알린다(오류 화면이 아니라) */
  const supplyDropped = !canSupply && (params.has('manufacturer') || params.get('in_stock') === 'true');

  const [res, setRes] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [draft, setDraft] = useState(q);
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const closeSave = useCallback(() => setSaving(false), []);
  /**
   * 고른 축(색 · 패턴 · 제조사)의 건수 — 그 축만 뺀 **지금 조건**으로 따로 센다(Codex 검토: 색을 고른 뒤 소재를 더하면
   * 색 견본 숫자가 이전 조건 값으로 남았다). 화면이 스스로 세는 검색이라 preview — 검색 기록에 남지 않는다.
   * 쪽만 바뀌면 다시 세지 않는다(axisKey).
   */
  const [axisFacets, setAxisFacets] = useState<Record<string, FacetEntry[]>>({});
  const axisKey = useRef<string>('');
  /** 지금 화면의 결과가 어느 질의 것인지 — 질의가 바뀌면 이전 결과를 흐리게 두지 않고 골격으로 */
  const resQ = useRef<string | null>(null);
  const [view, pickView] = useView();

  useEffect(() => { setDraft(q); }, [q]);
  // 0건 안내 청크(3KB)는 결과를 기다리는 동안 미리 받아 둔다 — 0건이 오면 바로 이유를 말하게
  useEffect(() => { void import('../components/ResultsEmpty').catch(() => undefined); }, []);

  // 리뷰 H4: 질의 · 페이지 · 필터 주소가 바뀔 때 한 번만 요청하고, 앞 요청은 취소한다
  useEffect(() => {
    if (waitRole) return;
    const ctl = new AbortController();
    const s = readSel(new URLSearchParams(key), canPrice, canSupply);
    setLoading(true);
    setError(null);
    const fkey = JSON.stringify([q, { ...s, page: undefined }]);
    const axes = AXES.filter((k) => s[k]);
    const auxP = fkey === axisKey.current ? Promise.resolve(null) : Promise.all(axes.map((k) =>
      search({ query: q, page: 1, pageSize: 1, preview: true, filters: filtersOf({ ...s, [k]: undefined }), signal: ctl.signal })
        .then((r) => [k, r.facets?.[k] ?? []] as const)
        .catch((e) => { if (isAbort(e)) throw e; return [k, null] as const; })));
    Promise.all([search({ query: q, page, filters: filtersOf(s), signal: ctl.signal }), auxP]).then(([r, ax]) => {
      if (ctl.signal.aborted) return;
      setRes(r);
      resQ.current = q;
      if (ax) {
        axisKey.current = fkey;
        setAxisFacets(Object.fromEntries(ax.filter((x): x is readonly [typeof x[0], FacetEntry[]] => x[1] != null)));
      }
      setLoading(false);
    }).catch((e) => {
      if (isAbort(e) || ctl.signal.aborted) return;
      setError(e);
      setLoading(false);
    });
    return () => ctl.abort();
  }, [key, q, page, retry, canPrice, canSupply, waitRole]);

  const hasCond = Boolean(sel.color || sel.pattern || sel.manufacturer || sel.in_stock || sel.materials.length || sel.price_max != null);

  const patch = (o: Record<string, string | undefined>) => {
    const n = new URLSearchParams(params);
    for (const [k, v] of Object.entries(o)) { if (v) n.set(k, v); else n.delete(k); }
    setParams(n);
  };
  const setPage = (p: number) => {
    patch({ page: p > 1 ? String(p) : undefined });
    window.scrollTo({ top: 0 });
  };
  /**
   * 조건 적용 — 「새 원단만(after)」 은 o.clearAfter 일 때만 지운다.
   * 잠긴 조건(비로그인의 제조사 · 재고, 운영자 미만의 단가)은 이 사람이 고를 칸이 없으니 일반 필터 변경으로는 주소에서 건드리지 않는다 —
   * o.clearLocked(「모두 지우기」) · 시트 「초기화」(next.cleared) 에서만 지운다(Codex 9차 ②)
   */
  const applySel = (next: Partial<Sel>, o: { clearAfter?: boolean; clearLocked?: boolean } = {}) => {
    const all = !!(o.clearLocked || next.cleared);
    patch({
      color: next.color, pattern: next.pattern, material: next.materials?.length ? next.materials.join(',') : undefined, page: undefined,
      ...(canSupply || all ? { manufacturer: next.manufacturer, in_stock: next.in_stock ? 'true' : undefined } : {}),
      ...(canPrice || all ? {
        price_max: next.price_max != null ? String(next.price_max) : undefined,
        currency: next.price_max != null ? next.currency : undefined,
      } : {}),
      ...(o.clearAfter ? { after: undefined } : {}),
    });
  };
  const setFacets = (next: FacetSelection) => applySel({ ...sel, ...next });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t) {
      // 10회차 m49 · Codex N3: 전체 원단(/browse)이 못 쓰는 조건(단가 · 새 원단만)이 있으면 검색어만 빼고 이 화면에 남는다 —
      // 저장한 검색 「검색 열기」 와 같은 규칙(조건이 조용히 빠지지 않게). 잠긴 단가(주소에만 있는)도 같다(Codex 9차 ②)
      if (sel.price_max != null || sel.after || params.has('price_max')) {
        const n = new URLSearchParams(params); n.delete('q'); n.delete('page');
        setParams(n);
        return;
      }
      // 9회차 M25: 검색어 없이 「검색」 → 같은 조건의 전체 원단(정렬 · 날짜 구분 · 여러 섬유가 있는 화면)
      const b = new URLSearchParams();
      for (const k of AXES) { const v = sel[k]; if (v) b.set(k, v); }
      if (sel.in_stock) b.set('in_stock', 'true');
      // 잠긴 재고 · 제조사는 주소 그대로 넘긴다 — 전체 원단도 같은 규칙으로 빼고 보여 주며 알린다(로그인하면 쓰인다)
      if (!canSupply) for (const k of ['manufacturer', 'in_stock']) { const v = params.get(k); if (v) b.set(k, v); }
      if (sel.materials.length) b.set('material', sel.materials.join(','));
      nav(b.toString() ? `/browse?${b.toString()}` : '/browse');
      return;
    }
    // 잠긴 조건(비로그인의 재고 · 제조사, 운영자 미만의 단가)은 검색어를 다시 · 새로 넣어도 주소에 이어 둔다 — 이 사람이 고를 칸이 없으니
    // 지우는 곳은 알림의 「… 조건 지우기」 · 「필터 초기화」 · 0건 「필터 해제」 뿐이다(Codex 10차 ①). 공개 필터는 예전처럼 새 검색에서 비운다
    const n = new URLSearchParams({ q: t });
    for (const k of [...(canSupply ? [] : ['manufacturer', 'in_stock']), ...(canPrice ? [] : ['price_max', 'currency'])]) {
      const v = params.get(k);
      if (v) n.set(k, v);
    }
    setParams(n);
  };

  const chips: AppliedChip[] = [];
  for (const k of AXES) {
    const v = sel[k];
    if (v) chips.push({ key: k, label: `${FIELD_KO[k]} · ${facetLabel(k, v)}`, clear: () => patch({ [k]: undefined, page: undefined }) });
  }
  for (const f of sel.materials) {
    chips.push({ key: `m-${f}`, label: `소재 · ${FIBER_KO[f]}`, clear: () => patch({ material: sel.materials.filter((x) => x !== f).join(',') || undefined, page: undefined }) });
  }
  if (sel.price_max != null) chips.push({ key: 'price', label: `단가 · ${sel.price_max.toLocaleString()} ${sel.currency} 이하`, clear: () => patch({ price_max: undefined, currency: undefined, page: undefined }) });
  if (sel.after) chips.push({ key: 'after', label: `새 원단만 · ${fmtDateTime(sel.after)} 이후`, clear: () => patch({ after: undefined, page: undefined }) });
  if (sel.in_stock) chips.push({ key: 'in_stock', label: '재고 보유', clear: () => patch({ in_stock: undefined, page: undefined }) });
  // 14회차 m64: 불러오는 동안의 레일 골격 칩 — 받은 뒤 레일(Facets)과 같은 순서(extraChips → 색 · 패턴 · 제조사 → 재고)
  const railChips = [
    ...chips.filter((c) => c.key.startsWith('m-') || c.key === 'price' || c.key === 'after'),
    ...chips.filter((c) => c.key === 'color' || c.key === 'pattern' || c.key === 'manufacturer'),
    ...chips.filter((c) => c.key === 'in_stock'),
  ].map((c) => c.label);
  const condText = chips.filter((c) => c.key !== 'after').map((c) => c.label).join(', ');

  const facetSel: FacetSelection = { color: sel.color, pattern: sel.pattern, manufacturer: sel.manufacturer, in_stock: sel.in_stock };
  const trailing = (s: Sel, set: (v: Sel) => void, inSheet: boolean) => (
    <>
      <MaterialFilter entries={res?.facets?.material ?? []} picked={s.materials} multi onChange={(v) => set({ ...s, materials: v })} />
      <Suspense fallback={null}>
        <PriceFilter mode="search" live={inSheet} value={{ price_max: s.price_max, currency: s.currency }}
                     onApply={(v) => set({ ...s, price_max: v.price_max, currency: v.currency })} />
      </Suspense>
    </>
  );
  // 질의가 바뀐 첫 요청이면 이전 결과 대신 골격, 같은 질의의 쪽 · 필터 변경이면 이전 결과를 흐리게 두고 바꾼다(밀림 없음)
  const showSkeleton = (loading || waitRole) && (!res || resQ.current !== q);
  const stale = loading && !showSkeleton;
  const facets = { ...(res?.facets ?? {}), ...axisFacets };
  const qHead = hasQ ? <>&ldquo;{q}&rdquo;</> : '검색어 없음';

  return (
    <div className="results-page page">
      {/* 9회차 관찰 ②: 낭독기의 제목 이동에 검색 결과 화면도 들어가게 */}
      <h1 className="sr-only">{hasQ ? `‘${q}’ 검색 결과` : '원단 검색 결과'}</h1>
      <form className="searchbar" role="search" onSubmit={submit}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="검색어" enterKeyHint="search"
               placeholder="색 · 패턴 · 느낌 · 품번 — 비우고 검색하면 전체 원단" />
        <button type="submit" className="btn primary"><Icon name="search" size={18} /> 검색</button>
      </form>

      {/* R3-4: 「검색 중…」 글자 대신 결과가 놓일 자리 — 해석 상자 · 필터 · 결과 머리 · 카드 격자 */}
      {showSkeleton && (
        <>
          <div className="interp skel-interp" aria-hidden="true"><span className="skel-line w-40" /><span className="skel-line w-60" /></div>
          <div className="results-layout">
            <div className="rail-wrap">
              <div className="rail-desk"><RailSkeleton chips={railChips} /></div>
              <div className="sheet-bar"><span className="btn sm sheet-open skel-btn" aria-hidden="true"><Icon name="sliders" size={16} /> 필터</span></div>
            </div>
            <section className="reslist" aria-label="검색 결과" aria-busy="true">
              <header className="res-head"><span className="res-q">{qHead}</span><span className="res-cnt skel-line w-24" aria-hidden="true" /></header>
              <GridSkeleton n={PAGE_SIZE} view={view} label="검색 중…" />
            </section>
          </div>
        </>
      )}

      {!loading && error != null && <ErrorState error={error} onRetry={() => setRetry((n) => n + 1)} />}

      {!showSkeleton && error == null && res && (
        <>
          <QueryInterp res={res} q={q} conds={chips.map((c) => c.label)} />

          <div className="results-layout">
            {/* 레일(≥768)과 아래 시트 버튼(<768)을 함께 두고 CSS 폭 조건으로 하나만 보인다 — 창 크기를 바꿔도 새로고침 없이 맞는다 */}
            <div className="rail-wrap">
              <FilterSheet<Sel> value={sel} empty={{ materials: [], after: sel.after, cleared: true }} chips={chips}
                           onApply={applySel}
                           preview={(d, signal) => search({ query: q, page: 1, pageSize: 1, preview: true, filters: filtersOf(d), signal }).then((r) => r.total)}>
                {(d, setD) => (
                  <Facets inSheet facets={facets} selected={{ color: d.color, pattern: d.pattern, manufacturer: d.manufacturer, in_stock: d.in_stock }}
                          supplyLocked={canSupply ? undefined : <SupplyLockedNote />}
                          onChange={(s) => setD({ ...d, ...s })} trailing={trailing(d, setD, true)} />
                )}
              </FilterSheet>
              <div className="rail-desk">
                <Facets facets={facets} selected={facetSel} onChange={setFacets}
                        supplyLocked={canSupply ? undefined : <SupplyLockedNote />}
                        trailing={trailing(sel, (v) => applySel(v), false)}
                        onReset={() => applySel({ materials: [] }, { clearLocked: true })}
                        extraChips={chips.filter((c) => c.key.startsWith('m-') || ['price', 'after'].includes(c.key))} />
              </div>
            </div>

            <section className="reslist" aria-label="검색 결과">
              <header className="res-head">
                <span className="res-q">{qHead}</span>
                <span className="res-cnt" aria-live="polite">
                  {res.total.toLocaleString()}건 · {fmtMs(res.took_ms)}
                  {res.reranked && <span className="tag">AI 재정렬</span>}
                </span>
                <span className="res-tools">
                  {/* 계획서 U17: 이 검색 저장 — 로그인한 사람만. 비로그인은 로그인 뒤 이 화면으로 돌아온다 */}
                  {user ? (
                    <button type="button" className="btn sm save-search-btn" onClick={() => setSaving(true)}>
                      <Icon name="bookmark-search" size={16} /> 이 검색 저장
                    </button>
                  ) : (
                    <Link className="btn sm ghost save-search-btn" to={loginHref(loc.pathname + loc.search)}>
                      <Icon name="bookmark-search" size={16} /> 로그인하고 이 검색 저장
                    </Link>
                  )}
                  {res.results.length > 0 && <ViewToggle view={view} onChange={pickView} />}
                </span>
              </header>
              {/* 잠긴 조건은 다른 필터를 바꿔도 주소에 남는다(Codex 9차 ②) — 그래서 알림에서 바로 지울 수 있게 */}
              {priceDropped && (
                <p className="res-note has-acts" role="note">
                  <span className="res-note-text"><Icon name="lock" size={14} />주소의 단가 조건은 운영자 이상만 쓸 수 있어 빼고 찾았습니다.</span>
                  <span className="res-note-acts">
                    <button type="button" className="btn sm ghost" onClick={() => patch({ price_max: undefined, currency: undefined, page: undefined })}>
                      <Icon name="close" size={16} /> 단가 조건 지우기
                    </button>
                  </span>
                </p>
              )}
              {supplyDropped && (
                <p className="res-note has-acts supply-dropped" role="note">
                  <span className="res-note-text"><Icon name="lock" size={14} />로그인하면 재고 · 제조사 조건을 쓸 수 있습니다 — 주소의 그 조건은 빼고 찾았습니다.</span>
                  <span className="res-note-acts">
                    <Link className="btn sm ghost" to={loginHref(loc.pathname + loc.search)}><Icon name="login" size={16} /> 로그인</Link>
                    <button type="button" className="btn sm ghost" onClick={() => patch({ manufacturer: undefined, in_stock: undefined, page: undefined })}>
                      <Icon name="close" size={16} /> 재고 · 제조사 조건 지우기
                    </button>
                  </span>
                </p>
              )}
              {stale && <p className="sr-only" role="status">결과를 바꾸는 중…</p>}

              {res.results.length === 0 ? (
                // R1-1: 0건이면 왜 없는지(서버 근거 · 고른 필터 · 새 원단만)와 다음 행동을 한 문장으로 — 9회차 m45
                // 대체 화면은 알림 영역이 아니다 — 낭독은 ResultsEmpty 가 확인을 마친 문구 한 번(10회차 관찰 ④)
                <Suspense fallback={<div className="state empty"><b>결과가 없습니다</b></div>}>
                  <ResultsEmpty q={q} res={res} conds={condText} hasCond={hasCond} after={sel.after}
                                onClearAll={() => applySel({ materials: [] }, { clearAfter: true, clearLocked: true })} />
                </Suspense>
              ) : (
                // 두 보기 모두 같은 ids 를 넘긴다 — 상세의 PREV / NEXT 가 결과 순서를 따른다
                <div className={`${view === 'list' ? 'res-list' : 'grid'}${stale ? ' is-stale' : ''}`}>
                  {res.results.map((item) => (
                    // 검색어가 없으면 최근 등록순 — 점수는 유사도가 아니라 숨긴다(10회차 m48)
                    <FabricCard key={item.fabric_id} item={item} variant={view} showScore={hasQ}
                                ids={res.results.map((x) => x.fabric_id)} />
                  ))}
                </div>
              )}

              <Pagination page={res.page} pageSize={res.page_size ?? PAGE_SIZE}
                          total={res.total} onChange={setPage} label="검색 결과 페이지" />
            </section>
          </div>
        </>
      )}

      {saving && (
        <Suspense fallback={null}>
          <SaveSearchDialog query={q} filters={{
            color: sel.color ?? null, pattern: sel.pattern ?? null, manufacturer: sel.manufacturer ?? null,
            in_stock: sel.in_stock ?? null, ...(sel.materials.length ? { materials: sel.materials } : {}),
            ...(sel.price_max != null ? { price_max: sel.price_max, currency: sel.currency ?? 'KRW' } : {}),
          }} onClose={closeSave} />
        </Suspense>
      )}
    </div>
  );
}
